import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import { dispatch } from '../store';

// Maps a non-OK callApi response to a friendly user-facing message and a
// toast severity. Returns null for responses that should be silent (401 is
// handled by the auth layer; 403 is kept silent because not every practice
// has WhatsApp configured and we don't want to alarm those users). Non-5xx
// friendly messages use `'warning'` so they do NOT capture to Sentry — 404s
// and 400s on stale account/phone IDs are expected user states, not bugs.
// 5xx keeps `success: false` so real backend failures still capture.
const getFriendlyWhatsappError = (response) => {
    if (response?.status === 401) return null;
    if (response?.status === 403) return null;
    if (response?.status === 404) {
        return { message: 'WhatsApp account not found. Please refresh and try again.', severity: 'warning' };
    }
    if (response?.status >= 500) {
        return { message: 'WhatsApp service is temporarily unavailable.', severity: false };
    }
    return {
        message: response?.data?.error || response?.data?.message || "Couldn't load WhatsApp account.",
        severity: 'warning',
    };
};

const initialState = {
    isLoading: false,
    whatsappAccountData: null,
    whatsappAccountPhonesData: null
};

const slice = createSlice({
    name: 'whatsappAccount',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        endLoading(state) {
            state.isLoading = false;
        },
        getAccountList(state, action) {
            state.isLoading = false;
            state.whatsappAccountData = action.payload;
        },
        getAccountPhonesList(state, action) {
            state.isLoading = false;
            state.whatsappAccountPhonesData = action.payload;
        },
    }
});

export default slice.reducer;

export const { clearMessage, reset, updateSubjectTemplateList, updateShowSubjectList } = slice.actions;

export function getWhatsappAccount(id) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/whatsapp-business-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.getAccountList(response.data));
                dispatch(slice.actions.endLoading());
            } else {
                const friendly = getFriendlyWhatsappError(response);
                if (friendly) {
                    dispatch(updateError({ message: friendly.message, success: friendly.severity }));
                }
                dispatch(slice.actions.endLoading());
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Network error', success: false }));
        } finally {
            dispatch(slice.actions.endLoading());
        }
    };
}

export function getWhatsappAccountPhones({ practiceId, id, handleSuccess }) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) { handleSuccess?.(); return; }
        // Guard: skip API call if business account PK is missing
        if (!practiceId || !id) { handleSuccess?.(); return; }
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/`);
            if (response.status === 200) {
                dispatch(slice.actions.getAccountPhonesList(response.data));
                handleSuccess({ success: true, phoneId: response.data.results });
                return;
            }
            const friendly = getFriendlyWhatsappError(response);
            if (friendly) {
                dispatch(updateError({ message: friendly.message, success: friendly.severity }));
            }
            handleSuccess();
        } catch (error) {
            dispatch(updateError({ message: error?.message, success: false }));
        }
    };
}

export function whatsappMessageSend({ practiceId, id, phoneId, messageData, handleSuccess, leadInfo }) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) { handleSuccess?.(); return; }
        try {
            const data = { ...messageData };
            let context = {};
            const respContextPractice = await callApi(
                `/api/v1/practices/${practiceId}/get_context/`,
                'GET'
            );
            const respContextLead = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/get_context/`,
                'GET'
            );
            const responseTemplateForLead = await callApi(`/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/treatments/`);
            const templateIdList = responseTemplateForLead?.data?.results?.map(dt => dt?.id) ?? [];
            let treatmentContext = {};
            // eslint-disable-next-line no-restricted-syntax
            for (const treatmentId of templateIdList) {
                // eslint-disable-next-line no-await-in-loop
                const treatMentContextResponse = await callApi(`/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/treatments/${treatmentId}/get_context/`);
                treatmentContext = { ...treatmentContext, ...treatMentContextResponse?.data };
            }
            context = {
                ...context, ...(respContextPractice?.data ?? {}), ...(respContextLead?.data ?? {}), ...(treatmentContext ?? {})
            };
            data.template_context = context;
            data.lead_id = leadInfo?.id;

            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/${phoneId}/send_message/`, 'post', data);
            if (response?.status === 200) {
                handleSuccess({ success: true });
                return;
            }
            const friendly = getFriendlyWhatsappError(response);
            if (friendly) {
                dispatch(updateError({ message: friendly.message, success: friendly.severity }));
            }
            handleSuccess();
        } catch (error) {
            handleSuccess();
            dispatch(updateError({ message: error?.message || 'Network error', success: false }));
        }
    };
}